import { useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAssistantStore } from '@/stores/assistantStore';
import { useAssistantChat } from '@/hooks/useAssistantChat';
import { ChatMessage } from './ChatMessage';
import { ConfirmationCard } from './ConfirmationCard';
import { UncertainOutcomeNotice } from './UncertainOutcomeNotice';

export function ChatMessageList() {
  const { t } = useTranslation('assistant');
  const messages = useAssistantStore((s) => s.messages);
  const isLoading = useAssistantStore((s) => s.isLoading);
  const pendingConfirmation = useAssistantStore((s) => s.pendingConfirmation);
  const confirmationRetry = useAssistantStore((s) => s.confirmationRetry);
  const settledActionIds = useAssistantStore((s) => s.settledActionIds);
  const { confirmAction, cancelAction } = useAssistantChat();
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, pendingConfirmation]);

  if (messages.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('welcome')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('welcomeDescription')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        px: 2,
        py: 1,
      }}
    >
      {messages.map((message) => {
        const showConfirmation =
          message.type === 'pending_confirmation' &&
          pendingConfirmation != null &&
          pendingConfirmation.actionId === message.actionId &&
          !settledActionIds.includes(pendingConfirmation.actionId);
        // Only `'unknown'` earns a notice. `'failed'` already says so in its
        // own content, and `'ok'` is the ordinary path.
        const showUncertainOutcome =
          message.type === 'action_confirmed' && message.outcome === 'unknown';

        return (
          <Box key={message.id}>
            <ChatMessage message={message} />
            {showUncertainOutcome && <UncertainOutcomeNotice />}
            {showConfirmation && (
              <ConfirmationCard
                description={pendingConfirmation.description}
                toolName={pendingConfirmation.toolName}
                presenter={pendingConfirmation.presenter}
                params={pendingConfirmation.params}
                retry={
                  confirmationRetry?.actionId === pendingConfirmation.actionId
                    ? confirmationRetry
                    : undefined
                }
                isLoading={isLoading}
                onConfirm={(fields) => void confirmAction(pendingConfirmation.actionId, fields)}
                onCancel={() => void cancelAction(pendingConfirmation.actionId)}
              />
            )}
          </Box>
        );
      })}

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 4.5, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            {t('thinking')}
          </Typography>
        </Box>
      )}

      <div ref={bottomRef} />
    </Box>
  );
}
